import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import SimpleSkeletonLoader from './SimpleSkeletonLoader';

const LazyComponentWrapper = ({ Component, componentProps = {}, options = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasTriggeredApiCalls, setHasTriggeredApiCalls] = useState(false);
  const containerRef = useRef(null);
  const componentRef = useRef(null);
  const prevAppointmentIdRef = useRef(componentProps.appointmentId);
  const prevPatientIdRef = useRef(componentProps.patientId);

  const {
    threshold = 0.1,
    rootMargin = '50px',
    triggerOnce = true
  } = options;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || (triggerOnce && hasLoaded)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (triggerOnce) {
              setHasLoaded(true);
              observer.unobserve(element);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce, hasLoaded]);

  // Callback ref to capture component instance
  const setComponentRef = (instance) => {
    componentRef.current = instance;
  };

  // Reset apiCalls trigger when appointment or patient changes
  useEffect(() => {
    if (
      prevAppointmentIdRef.current !== componentProps.appointmentId ||
      prevPatientIdRef.current !== componentProps.patientId
    ) {
      prevAppointmentIdRef.current = componentProps.appointmentId;
      prevPatientIdRef.current = componentProps.patientId;
      setHasTriggeredApiCalls(false);
      // Reset component's apiCallsTriggered flag if it exists
      if (componentRef.current && componentRef.current.setState) {
        componentRef.current.setState({ apiCallsTriggered: false });
      }
    }
  }, [componentProps.appointmentId, componentProps.patientId]);

  // Trigger apiCalls when component becomes visible (only once)
  useEffect(() => {
    if (isVisible && componentRef.current && !hasTriggeredApiCalls) {
      if (componentRef.current.apiCalls && typeof componentRef.current.apiCalls === 'function') {
        setHasTriggeredApiCalls(true);
        // Use setTimeout to ensure component is fully mounted
        setTimeout(() => {
          componentRef.current.apiCalls();
        }, 100);
      }
    }
  }, [isVisible, hasTriggeredApiCalls]);

  return (
    <Box ref={containerRef} component="div">
      {isVisible ? (
        <React.Suspense
          fallback={<SimpleSkeletonLoader height="15vw" />}
        >
          <Component ref={setComponentRef} {...componentProps} />
        </React.Suspense>
      ) : (
        // preserve layout with an empty box instead of the animated skeleton
        <Box sx={{ minHeight: "15vw" }} />
      )}
    </Box>
  );
};

export default LazyComponentWrapper;

